import type { TimelineFishGroup, TimelineStay } from '@/domain/fishGroups';

/** The open stay: where a live group is now. Null once it has left the site or ended. */
export function currentStay(group: TimelineFishGroup): TimelineStay | null {
    const last = group.stays.at(-1);
    return last && last.to === null ? last : null;
}

/** The stay it is in now, else the last one at this site. */
export function latestStay(group: TimelineFishGroup): TimelineStay | null {
    return group.stays.at(-1) ?? null;
}

/**
 * The bioscopes measuring the stay at `at` (now by default). An open stay keeps one placed by
 * then that is still there, including one whose placement ends later; a booking that starts
 * after `at` is left out. A closed stay keeps every bioscope that measured it. Ascending and
 * unique. A bioscope can only move with the fish from a placement that started before the
 * move, so the dialogs pass the move's date.
 */
export function stayBioscopeIds(stay: TimelineStay | null, at: Date = new Date()): number[] {
    if (!stay) return [];
    const coverage =
        stay.to === null
            ? stay.bioscopes.filter(
                  (cover) => cover.from <= at && (cover.to === null || cover.to > at)
              )
            : stay.bioscopes;
    return bioscopeIdsOf(coverage);
}

/**
 * Bioscopes that can move with the fish at `at`. Coverage at `at` is not enough: a placement
 * that already ended before `now` has a later open placement, and the bioscope move rejects a
 * time that is not after that open placement's start, rolling the fish move back with it. A
 * placement still open, or ending after `now`, can move when it was there at `at`.
 */
export function movableBioscopeIds(
    stay: TimelineStay | null,
    at: Date,
    now: Date = new Date()
): number[] {
    if (!stay || stay.to !== null) return [];
    return bioscopeIdsOf(
        stay.bioscopes.filter(
            (cover) =>
                cover.from <= at &&
                (cover.to === null || cover.to > at) &&
                (cover.to === null || cover.to > now)
        )
    );
}

function bioscopeIdsOf(coverage: { bioscopeId: number }[]): number[] {
    return [...new Set(coverage.map((cover) => cover.bioscopeId))].sort((a, b) => a - b);
}

/** The group's own start: its lifecycle start, else its first stay here. */
export function groupStartDate(group: TimelineFishGroup): Date | null {
    return group.lifecycleStartedAt ?? group.stays[0]?.from ?? null;
}

/** Live and stocked: a group registered for a later date has nothing to move or split yet. */
export function canRegisterEvents(group: TimelineFishGroup, now: Date): boolean {
    const stay = currentStay(group);
    return group.isLive && stay !== null && stay.from <= now;
}
